import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC, useMemo } from 'react';
import theme from 'src/assets/theme/theme';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import {
	ANTRAGS_FORMULAREN,
	FINANZIERUNG_PLAN_ERROR_MELDUNG,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PLATZHALTER_TEXT,
	VORHABENS_ART,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { Required, ValidDate } from 'src/lib/validation/ValidationTypes';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import {
	checkIfGroupsHaveNegativeValues,
	checkIfTotalsHaveNegativeValues,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { getAuFPruefAssistenzFormStruc, PruefAssistenzAUF } from 'src/utils/ausgabenFinanzierungHelper';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import { isUnternehmenHochschule } from 'src/pages/private/online-antrag/formulas/foerderantrag/LoeweAntragFormulas';
const KP_P = KostenPositionSchema.attributes;
const P = Schema.attributes;

const groupPropertiesToCheck: string[] = ['ausgabe', 'einnahme', 'gesamtAusgabe', 'gesamtEinnahme', 'foerderquote', 'averageFoerderquote'];
const totalsPropertiesToCheck: string[] = [
	'gesamtausgaben',
	'summeEinnahme',
	'summeZuwendung',
	'divisionZuwendungEinnahme',
	'gesamtGesamtausgaben',
	'gesamtSummeEinnahme',
	'gesamtSummeZuwendung',
	'gesamtDivisionZuwendungEinnahme',
];

const DEFAULT_MAX_FOERDERQUOTE = 100;
const VERBUNDPARTNER_HOCHSCHULE_MAX_FOERDERQUOTE = 90;
const VERBUNDPARTNER_UNTERNEHMEN_MAX_FOERDERQUOTE = 50;
const ANTRAGSTELLER_UNTERNEHMEN_MAX_FOERDERQUOTE = 50;

const getMaxFoerderquoteForGroup = (group: any): { maxFoerderquote: number; warningMessage: string } => {
	const unternehmenName = group.unternehmenName || 'Unbekanntes Unternehmen';
	let maxFoerderquote = DEFAULT_MAX_FOERDERQUOTE;
	let maxWarningMessage = '';

	if (group.kostenTraeger === KOSTENTRAEGER.Verbundpartner) {
		if (group.hochschule) {
			maxFoerderquote = VERBUNDPARTNER_HOCHSCHULE_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${unternehmenName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigen- und Drittmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		} else {
			maxFoerderquote = VERBUNDPARTNER_UNTERNEHMEN_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${unternehmenName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigenmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		}
	} else if (group.kostenTraeger === KOSTENTRAEGER.Antragsteller) {
		if (group.hochschule) {
			maxFoerderquote = DEFAULT_MAX_FOERDERQUOTE;
		} else {
			maxFoerderquote = ANTRAGSTELLER_UNTERNEHMEN_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${unternehmenName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigenmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		}
	}

	return {
		maxFoerderquote,
		warningMessage: maxWarningMessage,
	};
};

const FinanzierungsplanGlobal: FC<{}> = () => {
	const { application } = useApplication();

	const JahrDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path);
	const AllYears = Array.from({ length: JahrDauer }, (_, i) => get(application, P.Allgemein_Projektstart_Jahr.path) + i);

	const allKostenPosition = useMemo(() => {
		return get(application, P.Foerderantrag_Kostenpositions.path);
	}, [application]);

	const { groupedData: transfromedRows, totals } = transformData(
		allKostenPosition,
		JahrDauer,
		get(application, P.Allgemein_Projektstart_Jahr.path),
		application,
	);

	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(transfromedRows, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);

	const antragsformular = get(application, P.Allgemein_Antragsformular.path);
	const hasFoerderquoteMaxViolation = useMemo(() => {
		if (!(antragsformular === ANTRAGS_FORMULAREN.Loewe)) {
			return [];
		}

		return Object.values(transfromedRows).reduce((warnings: string[], group: any) => {
			const foerderquote = parseFloat(group.averageFoerderquote) || 0;
			const { maxFoerderquote, warningMessage } = getMaxFoerderquoteForGroup(group);
			if (foerderquote > maxFoerderquote) {
				warnings.push(warningMessage);
			}

			return warnings;
		}, []);
	}, [antragsformular, transfromedRows]);

	return (
		<>
			<ApplicationHeading title='Ausgaben- und Finanzierungsplan' description={PLATZHALTER_TEXT} />
			{hasNegativeValues ?
				<>
					<MUIAlert infoType={'error'} title={'Warnung'} message={FINANZIERUNG_PLAN_ERROR_MELDUNG} />
				</>
			:	null}
			{hasFoerderquoteMaxViolation.length > 0 ?
				hasFoerderquoteMaxViolation.map((warningText: string, index: number) => (
					<MUIAlert key={`foerderquote-warning-${index}`} infoType={'warning'} title={'Achtung:'} message={warningText} />
				))
			:	null}
			<TableContainer>
				<Table size='medium' aria-label='a dense table'>
					<TableHead>
						<TableRow>
							<TableCell></TableCell>
							{AllYears.map((year: any) => (
								<TableCell key={year}>{year}</TableCell>
							))}
							<TableCell>Gesamt</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{Object.values(transfromedRows).map((group: any, index: number) => (
							<React.Fragment key={index}>
								<TableRow>
									<TableCell colSpan={JahrDauer + 2} sx={{ backgroundColor: theme.palette.background.paper }}>
										{group.unternehmenName}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Ausgaben</TableCell>
									{group.ausgabe.map((value: any, index: number) => (
										<TableCell
											key={index}
											style={{
												color: parseFloat(value) < 0 ? 'red' : 'inherit',
											}}
										>
											{DisplayEuro(value.toFixed(2))}
										</TableCell>
									))}
									<TableCell
										style={{
											color: parseFloat(group.gesamtAusgabe) < 0 ? 'red' : 'inherit',
										}}
									>
										{DisplayEuro(group.gesamtAusgabe)}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Eigen- und Drittmittel</TableCell>
									{group.einnahme.map((value: any, index: number) => (
										<TableCell
											key={index}
											style={{
												color: parseFloat(value) < 0 ? 'red' : 'inherit',
											}}
										>
											{DisplayEuro(value.toFixed(2))}
										</TableCell>
									))}
									<TableCell
										style={{
											color: parseFloat(group.gesamtEinnahme) < 0 ? 'red' : 'inherit',
										}}
									>
										{DisplayEuro(group.gesamtEinnahme)}
									</TableCell>
								</TableRow>
								{get(application, P.Allgemein_Vorhabensart.path) === VORHABENS_ART.Verbundvorhaben && (
									<TableRow>
										<TableCell>Individuelle Förderquote</TableCell>
										{group.foerderquote.map((value: any, index: number) => (
											<TableCell
												key={index}
												style={{
													color: parseFloat(value) < 0 ? 'red' : 'inherit',
												}}
											>
												{value > 0 ? value : 0}%
											</TableCell>
										))}
										<TableCell
											style={{
												color: parseFloat(group.averageFoerderquote) < 0 ? 'red' : 'inherit',
											}}
										>
											{group.averageFoerderquote > 0 ? group.averageFoerderquote : 0}%
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
						<TableRow className='total-row'>
							<TableCell>Gesamtausgaben</TableCell>
							{totals.gesamtausgaben.map((value: any, index: number) => (
								<TableCell key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit' }}>
									{DisplayEuro(value.toFixed(2))}
								</TableCell>
							))}
							<TableCell
								style={{
									color: parseFloat(totals.gesamtGesamtausgaben) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtGesamtausgaben)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell>Summe Eigen- und Drittmittel</TableCell>
							{totals.summeEinnahme.map((value: any, index: number) => (
								<TableCell key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit' }}>
									{DisplayEuro(value.toFixed(2))}
								</TableCell>
							))}
							<TableCell
								style={{
									color: parseFloat(totals.gesamtSummeEinnahme) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtSummeEinnahme)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell>Beantragte Förderung</TableCell>
							{totals.summeZuwendung.map((value: any, index: number) => (
								<TableCell key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit' }}>
									{DisplayEuro(value)}
								</TableCell>
							))}
							<TableCell
								style={{
									color: parseFloat(totals.gesamtSummeZuwendung) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtSummeZuwendung)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell>Beantragte Förderquote</TableCell>
							{totals.divisionZuwendungEinnahme.map((value: any, index: number) => (
								<TableCell key={index} style={{ color: parseFloat(value) < 0 ? 'red' : 'inherit' }}>
									{value > 0 ? value : 0}%
								</TableCell>
							))}
							<TableCell
								style={{
									color: parseFloat(totals.gesamtDivisionZuwendungEinnahme) < 0 ? 'red' : 'inherit',
								}}
							>
								{totals.gesamtDivisionZuwendungEinnahme > 0 ? totals.gesamtDivisionZuwendungEinnahme : 0}%
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
			<PruefAssistenzAUF />
		</>
	);
};

export default FinanzierungsplanGlobal;

export function transformData(kostenPositions: any, JahrDauer: number, ProjektStart: number, application?: any) {
	const groupedData: any = {};
	const allKostenPositionen = kostenPositions || [];

	// Group by Unternehmen.id
	allKostenPositionen.forEach((eachKP: any) => {
		const unternehmenID = get(eachKP, `${KP_P.Unternehmen.path}.id`);
		const shouldSetHochschule = Boolean(application);
		const isUnternehmen = shouldSetHochschule ? isUnternehmenHochschule(eachKP, application) : undefined;
		const unternehmenName =
			shouldSetHochschule && isUnternehmen ?
				get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Kuerzel.path, '') ||
				get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '')
			:	get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '');

		const kostenTraeger = get(eachKP, KP_P.Allgemein_Kostentraeger.path, '');

		if (!groupedData[unternehmenID]) {
			groupedData[unternehmenID] = {
				unternehmenName,
				...(shouldSetHochschule ? { hochschule: Boolean(isUnternehmen) } : {}),
				...(shouldSetHochschule ? { kostenTraeger: kostenTraeger } : {}),
				ausgabe: Array(JahrDauer).fill(0),
				einnahme: Array(JahrDauer).fill(0),
			};
		}

		get(eachKP, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).forEach((jahr: any, _index: number) => {
			const yearIndex = jahr.Zelle_1 - ProjektStart; // Assuming starting year is 2024
			if (get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe) {
				groupedData[unternehmenID].ausgabe[yearIndex] += parseFloat(jahr.Zelle_2) || 0;
			} else if (get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme) {
				groupedData[unternehmenID].einnahme[yearIndex] += parseFloat(jahr.Zelle_2) || 0;
			}
		});
	});

	// Calculate Gesamt and individuelle Förderquote
	Object.values(groupedData).forEach((group: any) => {
		group.gesamtAusgabe = group.ausgabe.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
		group.gesamtEinnahme = group.einnahme.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);

		group.foerderquote = group.ausgabe.map((ausgabe: any, index: number) => {
			const einnahme = group.einnahme[index] || 0;
			return (((parseFloat(ausgabe) - parseFloat(einnahme)) / parseFloat(ausgabe)) * 100).toFixed(2);
		});

		// group.averageFoerderquote = (
		// 	group.foerderquote.reduce((acc: any, val: any) => acc + parseFloat(val), 0) / JahrDauer
		// ).toFixed(2);

		Object.values(groupedData).forEach((group: any) => {
			group.averageFoerderquote = (((group.gesamtAusgabe - group.gesamtEinnahme) / group.gesamtAusgabe) * 100).toFixed(2);
		});
	});

	// Calculate overall totals for the last four rows
	const totals: any = {
		gesamtausgaben: Array(JahrDauer).fill(0),
		summeEinnahme: Array(JahrDauer).fill(0),
		summeZuwendung: Array(JahrDauer).fill(0),
		divisionZuwendungEinnahme: Array(JahrDauer).fill(0),
	};

	Object.values(groupedData).forEach((group: any) => {
		group.ausgabe.forEach((value: any, index: number) => {
			totals.gesamtausgaben[index] += value;
		});
		group.einnahme.forEach((value: any, index: number) => {
			totals.summeEinnahme[index] += value;
		});
	});

	totals.summeZuwendung = totals.gesamtausgaben.map((value: any, index: number) => {
		return (value - totals.summeEinnahme[index]).toFixed(2);
	});

	totals.divisionZuwendungEinnahme = totals.summeZuwendung.map((value: any, index: number) => {
		const ausgabe = totals.gesamtausgaben[index];
		return ausgabe ? ((parseFloat(value) / ausgabe) * 100).toFixed(2) : '0.00';
	});

	totals.gesamtGesamtausgaben = totals.gesamtausgaben.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
	totals.gesamtSummeEinnahme = totals.summeEinnahme.reduce((acc: any, val: any) => acc + val, 0).toFixed(2);
	totals.gesamtSummeZuwendung = totals.summeZuwendung.reduce((acc: any, val: any) => acc + parseFloat(val), 0).toFixed(2);
	totals.gesamtDivisionZuwendungEinnahme = ((parseFloat(totals.gesamtSummeZuwendung) / parseFloat(totals.gesamtGesamtausgaben)) * 100).toFixed(2);

	return { groupedData, totals };
}

const formStrucAlwaysFalse = () => {
	return [
		{
			fields: [
				{
					...P.Allgemein_Projektdauerin_Jahren,
					validation: [{ validateFunction: Required }, { validateFunction: ValidDate }],
				},
			],
		},
	];
};

export const getFormStrucFinanzierungsplanGlobal = (application: any) => {
	let result: FormStrucItem[] = [];
	const JahrDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path);

	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path);

	const { groupedData: transfromedRows, totals } = transformData(allKostenPosition, JahrDauer, get(application, P.Allgemein_Projektstart_Jahr.path));

	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(transfromedRows, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);

	if (hasNegativeValues) {
		result = concat(result, formStrucAlwaysFalse());
	}
	result = getAuFPruefAssistenzFormStruc(application, result);
	return result;
};
